import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Typography,
  TablePagination,
  useTheme,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import {
  getCategoryFileCount,
  getFileStatusCount,
  getFilesByStatus,
  deleteFile,
  updateFile,
} from "../../../../core/repo/fileRepo";
import Loader from "../../../../components/Loader";
import StatusMetrics from "./StatusMetrics";
import FilesList from "./FilesList/FilesList";
import { enqueueSnackbar } from "notistack";
import {
  DELETE_FILE_SUCCESS,
  DELETE_FILE_FAILURE,
  EDIT_FILE_SUCCESS,
  EDIT_FILE_FAILURE,
  SELECT_CATEGORY_ON_DATA_MANAGEMENT,
  CLEAR_ALL_CATEGORIES_ON_DATA_MANAGEMENT,
  DELETE_CATEGORY_ON_DATA_MANAGEMENT,
  SELECT_STATUS_ON_DATA_MANAGEMENT,
  PAGE_CHANGE_ON_DATA_MANAGEMENT,
} from "../../../../utils/posthogEvents";
import posthog from "posthog-js";
import CloseIcon from "@mui/icons-material/Close";
import { VIEW_FILE_URL } from "../../../../utils/routes";

function ProcessingPipeline() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [categoryFileCount, setCategoryFileCount] = useState(null);
  const [fileStatusCount, setFileStatusCount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [filesData, setFilesData] = useState([]);
  const [page, setPage] = useState(0);
  const { selectedDomain } = useSelector((state) => state.domain);

  useEffect(() => {
    // Get initial values from URL query params
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get("category");
    const statusParam = searchParams.get("status");
    const pageParam = searchParams.get("page");

    if (categoryParam) {
      setSelectedCategories(categoryParam.split(","));
    }
    if (statusParam) {
      setSelectedStatus(statusParam);
    }
    if (pageParam) {
      setPage(parseInt(pageParam));
    }

    fetchData(
      categoryParam ? categoryParam.split(",") : [],
      statusParam || "all",
      pageParam ? parseInt(pageParam) : page
    );
  }, [selectedDomain]);

  const updateUrlParams = (categories, status, page) => {
    const searchParams = new URLSearchParams(location.search);
    if (
      categories &&
      categories.length > 0 &&
      !(categories.length === 1 && categories[0] === "All")
    ) {
      searchParams.set("category", categories.join(","));
    } else {
      searchParams.delete("category");
    }
    if (status && status !== "all") {
      searchParams.set("status", status);
    } else {
      searchParams.delete("status");
    }
    if (page !== undefined && page !== null) {
      searchParams.set("page", page);
    } else {
      searchParams.delete("page");
    }
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const fetchData = async (categories, status, page) => {
    try {
      setIsLoading(true);
      const categoryFileCount = await dispatch(
        getCategoryFileCount(selectedDomain.id)
      );
      let tempCategories = categories.filter((c) => c !== "All").join(",");
      const tempStatus = status === "all" ? "" : status;
      const fileStatusCount = await dispatch(
        getFileStatusCount(tempCategories, selectedDomain.id)
      );
      const filesResponse = await dispatch(
        getFilesByStatus(
          tempStatus,
          tempCategories,
          page + 1,
          selectedDomain.id
        )
      );
      setCategoryFileCount(categoryFileCount);
      setFileStatusCount(fileStatusCount);
      setFilesData(filesResponse);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  function handleCategoryChange(event) {
    const value = event.target.value;
    let categories = typeof value === "string" ? value.split(",") : value;
    if (categories.includes("All") && categories.length > 1) {
      categories = categories.filter((c) => c !== "All");
    }
    posthog.capture(SELECT_CATEGORY_ON_DATA_MANAGEMENT, {
      category: categories,
    });
    setSelectedCategories(categories);
    updateUrlParams(categories, selectedStatus, page);
    fetchData(categories, selectedStatus, page);
  }

  function handleStatusChange(status) {
    posthog.capture(SELECT_STATUS_ON_DATA_MANAGEMENT, {
      status: status,
    });
    setSelectedStatus(status);
    updateUrlParams(selectedCategories, status, page);
    fetchData(selectedCategories, status, page);
  }

  function handleChangePage(event, newPage) {
    posthog.capture(PAGE_CHANGE_ON_DATA_MANAGEMENT, {
      page: newPage,
    });
    setPage(newPage);
    updateUrlParams(selectedCategories, selectedStatus, newPage);
    fetchData(selectedCategories, selectedStatus, newPage);
  }

  function handleDeleteCategory(category) {
    posthog.capture(DELETE_CATEGORY_ON_DATA_MANAGEMENT, {
      category: category,
    });
    const newCategories = selectedCategories.filter((c) => c !== category);
    setSelectedCategories(newCategories);
    updateUrlParams(newCategories, selectedStatus, page);
    fetchData(newCategories, selectedStatus, page);
  }

  function handleClearAllCategories() {
    posthog.capture(CLEAR_ALL_CATEGORIES_ON_DATA_MANAGEMENT);
    setSelectedCategories([]);
    updateUrlParams([], selectedStatus, page);
    fetchData([], selectedStatus, page);
  }

  const handleDeleteFile = async (fileId) => {
    try {
      await dispatch(deleteFile(fileId));
      enqueueSnackbar("File has been deleted", {
        variant: "success",
      });
      fetchData(selectedCategories, selectedStatus, page);
      posthog.capture(DELETE_FILE_SUCCESS);
    } catch (e) {
      enqueueSnackbar("Failed to delete file", {
        variant: "error",
      });
      posthog.capture(DELETE_FILE_FAILURE);
      console.log(e);
    }
  };

  const handleUpdateFile = async (file) => {
    try {
      await dispatch(updateFile(file));
      setFilesData({
        ...filesData,
        files: filesData?.files?.map((f) =>
          f.id === file.id ? { ...f, ...file } : f
        ),
      });
      enqueueSnackbar("File name has been updated", {
        variant: "success",
      });
      posthog.capture(EDIT_FILE_SUCCESS);
    } catch (e) {
      enqueueSnackbar("Failed to update file", {
        variant: "error",
      });
      console.log(e);
      posthog.capture(EDIT_FILE_FAILURE);
    }
  };

  const handleSelectFile = (file) => {
    const { id, file_type, file_url } = file;
    if (file_type === "docx") {
      window.open(file_url, "_blank", "noopener,noreferrer");
    } else {
      // const fileViewUrl = `${window.location.origin}${VIEW_FILE_URL}/${id}`;
      // window.open(fileViewUrl, "_blank", "noopener,noreferrer");
      history.push(`${VIEW_FILE_URL}/${id}`);
    }
  };

  return (
    <Box flex={1} overflow={"hidden"} display={"flex"} flexDirection={"column"}>
      {categoryFileCount && (
        <Box
          px={2}
          pt={3}
          pb={2}
          sx={{ flex: 1, overflow: "hidden" }}
          display={"flex"}
          flexDirection="column"
          gap={0}>
          {/* Header row: Status title and category dropdown */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={3}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Overall Pipeline Status
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Filter by File Category:
              </Typography>
              <Select
                multiple
                displayEmpty
                value={selectedCategories}
                onChange={handleCategoryChange}
                input={<OutlinedInput />}
                renderValue={(selected) => {
                  if (!selected.length) {
                    return (
                      <span style={{ color: "#666" }}>Multiple Categories</span>
                    );
                  }
                  if (selected.length === 1) {
                    return selected[0];
                  }
                  return `${selected.length} categories selected`;
                }}
                sx={{
                  minWidth: 260,
                  maxWidth: 340,
                  background: theme.palette.background.default,
                  borderRadius: 2,
                  "& .MuiSelect-select": {
                    paddingY: 1.5,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 320,
                      minWidth: 280,
                      borderRadius: 8,
                    },
                  },
                }}>
                {Object.keys(categoryFileCount)
                  .filter((category) => category !== "All") // Hide "All" from dropdown
                  .map((category) => (
                    <MenuItem key={category} value={category}>
                      <Checkbox
                        checked={selectedCategories.indexOf(category) > -1}
                        size="small"
                      />
                      <ListItemText
                        primary={
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%">
                            <Typography variant="body2">{category}</Typography>
                            <Chip
                              size="small"
                              label={categoryFileCount[category]}
                              sx={{ ml: 1, minWidth: 35 }}
                            />
                          </Box>
                        }
                      />
                    </MenuItem>
                  ))}
              </Select>
            </Box>
          </Box>

          {/* Chips for selected categories */}
          {selectedCategories.length > 0 && (
            <Box
              display="flex"
              gap={1}
              mb={3}
              flexWrap="wrap"
              alignItems="center">
              {selectedCategories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  onDelete={() => handleDeleteCategory(category)}
                  deleteIcon={<CloseIcon />}
                  sx={{
                    fontWeight: 500,
                    backgroundColor: theme.palette.background.light,
                  }}
                />
              ))}
              <Chip
                label="CLEAR FILTERS"
                onClick={handleClearAllCategories}
                color="primary"
                variant="outlined"
                sx={{
                  fontWeight: 500,
                  textTransform: "uppercase",
                }}
              />
            </Box>
          )}

          <Box flex={1} overflow={"auto"}>
            <StatusMetrics
              fileStatusCount={fileStatusCount}
              selectedStatus={selectedStatus}
              onStatusChange={handleStatusChange}
            />
            <Box mb={4} />
            <Box
              p={2}
              pb={0}
              borderRadius={4}
              border={`1px solid ${theme.palette.divider}`}>
              <FilesList
                selectedStatus={selectedStatus}
                filesData={filesData}
                handleSelectFile={handleSelectFile}
                handleDeleteFile={handleDeleteFile}
                handleUpdateFile={handleUpdateFile}
              />
              {filesData?.total > 0 && (
                <TablePagination
                  component={Box}
                  count={filesData?.total}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={50}
                  rowsPerPageOptions={[50]}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}
      {isLoading && <Loader />}
    </Box>
  );
}

export default ProcessingPipeline;
